import React, { JSX } from 'react'
import { fitToObjectFit } from 'components/BackgroundImageInput'
import { CenteredOnCanvas } from 'components/ScaledStage'
import { TWITCH_MARK_SRC, YOUTUBE_MARK_SRC } from 'components/chatOverlays/platformMarks'
import { POLL, elementFontStack, elementPinnedWeight } from 'lib/elementCanvas'
import { ChatFontKey, normalizeChatFontKey } from 'lib/chatFonts'
import { useFontGate } from 'lib/fontLoader'
import {
  VoteLaneMode,
  largestRemainderPercents,
  splitWinnersOf,
  sumCounts,
  twitchChannelCountOf,
  voteLaneModeOf
} from 'lib/votePercent'
import {
  ImageFit,
  PollSummary,
  PollTemplate,
  TickerTextSize,
  VoteCountsByPlatform
} from 'types/api'

const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const DEFAULT_WINNER_OUTLINE_COLOR = '#67E8F9'

const PILL_FONT_SIZE = 16
const PILL_ICON_SIZE = 13
const round2 = (x: number): number => Math.round(x * 100) / 100
const scaleBy = (px: number, base: number, baseFont: number): number => round2((px * base) / baseFont)

export const pollTextSizeDefaults = (template: PollTemplate, tickerTextSize: TickerTextSize): {
  question: number
  tickerLabel: number
  displayText: number
  command: number
  number: number
  callToAction: number
  closedText: number
} => template === 'TICKER'
  ? {
      question: POLL.ticker.left.question.fontSize,
      tickerLabel: POLL.ticker.left.eyebrow.fontSize,
      displayText: POLL.ticker.segment.title[tickerTextSize].fontSize,
      command: POLL.ticker.segment.chip.fontSize,
      number: POLL.ticker.segment.percent.fontSize,
      callToAction: POLL.ticker.cta.fontSize,
      closedText: PILL_FONT_SIZE
    }
  : {
      question: POLL.heading.fontSize,
      tickerLabel: POLL.ticker.left.eyebrow.fontSize,
      displayText: POLL.option.fontSize,
      command: POLL.option.fontSize,
      number: POLL.option.fontSize,
      callToAction: POLL.callToAction.fontSize,
      closedText: PILL_FONT_SIZE
    }

type Widen<T> = T extends number ? number : T extends string ? string : { -readonly [K in keyof T]: Widen<T[K]> }
type TickerMetrics = Widen<typeof POLL.ticker>

export interface PollDraft {
  name: string
  question: string
  callToActionText: string
  options: Array<{
    displayText: string
    command: string
    textColor: string
    progressBarColor: string
    backgroundColor: string
  }>
  template: PollTemplate
  tickerLabel: string
  ctaSubtext: string
  tickerTextSize: TickerTextSize
  tickerBlockWeights?: number[] | null
  headingTextColor: string
  headingBackground: string
  optionsTextColor: string
  optionsBackground: string
  callToActionTextColor: string
  callToActionBackground: string
  alternatingOptionBackground: string
  backgroundImage: string
  backgroundImageFit: ImageFit
  cornerRadius: number
  borderWidth: number
  borderColor: string
  winnerOutlineColor: string
  fontKey: ChatFontKey
  questionFontSize?: number | null
  tickerLabelFontSize?: number | null
  displayTextFontSize?: number | null
  commandFontSize?: number | null
  numberFontSize?: number | null
  callToActionFontSize?: number | null
  closedTextFontSize?: number | null
  tickerHeight?: number | null
}

const tickerMetricsFor = (draft: PollDraft): typeof POLL.ticker | TickerMetrics => {
  const q = draft.questionFontSize
  const label = draft.tickerLabelFontSize
  const title = draft.displayTextFontSize
  const cmd = draft.commandFontSize
  const num = draft.numberFontSize
  const cta = draft.callToActionFontSize
  const height = draft.tickerHeight
  const base = POLL.ticker
  if (q == null && label == null && title == null && cmd == null && num == null && cta == null && height == null) return base
  const step = base.segment.title[draft.tickerTextSize]
  return {
    ...base,
    minHeight: height == null ? base.minHeight : Math.max(0, height - 2 * (draft.borderWidth ?? DEFAULT_BORDER_WIDTH)),
    left: {
      ...base.left,
      eyebrow: label == null
        ? base.left.eyebrow
        : {
            ...base.left.eyebrow,
            fontSize: label,
            lineHeight: scaleBy(label, base.left.eyebrow.lineHeight, base.left.eyebrow.fontSize),
            dotSize: scaleBy(label, base.left.eyebrow.dotSize, base.left.eyebrow.fontSize)
          },
      question: q == null
        ? base.left.question
        : { fontSize: q, lineHeight: scaleBy(q, base.left.question.lineHeight, base.left.question.fontSize) }
    },
    segment: {
      ...base.segment,
      chip: cmd == null
        ? base.segment.chip
        : {
            ...base.segment.chip,
            fontSize: cmd,
            height: scaleBy(cmd, base.segment.chip.height, base.segment.chip.fontSize),
            minWidth: scaleBy(cmd, base.segment.chip.minWidth, base.segment.chip.fontSize)
          },
      title: title == null
        ? base.segment.title
        : {
            ...base.segment.title,
            [draft.tickerTextSize]: { ...step, fontSize: title, lineHeight: scaleBy(title, step.lineHeight, step.fontSize) }
          },
      percent: num == null
        ? base.segment.percent
        : { ...base.segment.percent, fontSize: num, lineHeight: scaleBy(num, base.segment.percent.lineHeight, base.segment.percent.fontSize) }
    },
    cta: cta == null
      ? base.cta
      : {
          ...base.cta,
          fontSize: cta,
          lineHeight: scaleBy(cta, base.cta.lineHeight, base.cta.fontSize),
          subtext: {
            ...base.cta.subtext,
            fontSize: scaleBy(cta, base.cta.subtext.fontSize, base.cta.fontSize),
            lineHeight: scaleBy(cta, base.cta.subtext.lineHeight, base.cta.fontSize)
          }
        }
  }
}

const classicSpanSize = (px: number | null | undefined): { fontSize?: string, lineHeight?: string } => px == null
  ? {}
  : { fontSize: `${px}px`, lineHeight: `${scaleBy(px, POLL.option.lineHeight, POLL.option.fontSize)}px` }

export interface PollLiveData {
  totalVotes: number
  optionVoteCounts: number[]
  optionVoteCountsByPlatform?: VoteCountsByPlatform[]
  hasYouTubeVotes?: boolean
  votingClosed: boolean
  laneMode?: VoteLaneMode
}

export const pollHasYouTubeVotes = (poll: PollSummary): boolean =>
  (poll.hasYouTubeVotes ?? false) ||
  poll.options.some((o) => (o.voteCountByPlatform?.youtube ?? 0) > 0)

export const pollLiveFromSummary = (poll: PollSummary): PollLiveData => {
  const byPlatform = poll.options.map(
    (o) => o.voteCountByPlatform ?? { twitch: o.voteCount, youtube: 0 }
  )
  const youtubeCounts = byPlatform.map((c) => c.youtube)
  const laneMode = voteLaneModeOf({
    twitchChannelCount: twitchChannelCountOf(poll.broadcasterUserIds),
    twitchTotal: sumCounts(byPlatform.map((c) => c.twitch)),
    youtubeTotal: sumCounts(youtubeCounts)
  })
  const youtubeOnly = laneMode === 'YOUTUBE'
  return {
    totalVotes: youtubeOnly ? sumCounts(youtubeCounts) : poll.totalVotes,
    optionVoteCounts: youtubeOnly ? youtubeCounts : poll.options.map((o) => o.voteCount),
    optionVoteCountsByPlatform: byPlatform,
    hasYouTubeVotes: pollHasYouTubeVotes(poll),
    votingClosed: poll.status !== 'ACTIVE',
    laneMode
  }
}

type LanePlatform = 'twitch' | 'youtube'
const LANE_DIM_OPACITY = 0.45
const LANE_YOUTUBE_FILL_OPACITY = 0.6
const SPLIT_CLASSIC = { laneGap: 4, laneMarkHeight: 12, markColumn: 15, markGap: 6, figureMarkHeight: 14, winnerMarkHeight: 16 }
const SPLIT_TICKER = { laneGap: 3, laneMarkHeight: 10, markColumn: 12, markGap: 5, figureMarkHeight: 11, winnerMarkHeight: 12 }

const VoteMark = ({ platform, height, gapBefore = 0 }: { platform: LanePlatform, height: number, gapBefore?: number }): JSX.Element => (
  <img
    src={platform === 'twitch' ? TWITCH_MARK_SRC : YOUTUBE_MARK_SRC}
    alt={platform === 'twitch' ? 'Twitch' : 'YouTube'}
    data-vote-mark={platform}
    style={{
      display: 'inline-block',
      width: 'auto',
      height: `${height}px`,
      flexShrink: 0,
      marginLeft: gapBefore > 0 ? `${gapBefore}px` : undefined,
      verticalAlign: 'middle',
      filter: 'none',
      opacity: 1,
      transform: 'none',
      mixBlendMode: 'normal'
    }}
  />
)

const dimOf = (dimmed: boolean): React.CSSProperties => ({
  opacity: dimmed ? LANE_DIM_OPACITY : 1,
  transition: 'opacity 350ms ease'
})

const SplitFigures = ({ twitchPct, youtubePct, markHeight, dimmed }: {
  twitchPct: number
  youtubePct: number
  markHeight: number
  dimmed: boolean
}): JSX.Element => (
  <>
    <VoteMark platform='twitch' height={markHeight} />
    <span data-lane-pct='twitch' style={{ marginLeft: '5px', ...dimOf(dimmed) }}>{`${twitchPct}%`}</span>
    <VoteMark platform='youtube' height={markHeight} gapBefore={12} />
    <span data-lane-pct='youtube' style={{ marginLeft: '5px', ...dimOf(dimmed) }}>{`${youtubePct}%`}</span>
  </>
)

const VoteLane = ({ platform, pct, color, trackHeight, metrics, glint, dimmed }: {
  platform: LanePlatform
  pct: number
  color: string
  trackHeight: number
  metrics: typeof SPLIT_CLASSIC
  glint: boolean
  dimmed: boolean
}): JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center', gap: `${metrics.markGap}px` }}>
    <span
      style={{
        flex: `0 0 ${metrics.markColumn}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: `${Math.max(trackHeight, metrics.laneMarkHeight)}px`
      }}
    >
      <VoteMark platform={platform} height={metrics.laneMarkHeight} />
    </span>
    <div
      data-lane-track={platform}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: `${trackHeight}px`,
        borderRadius: '9999px',
        background: 'rgba(255,255,255,0.15)',
        overflow: 'hidden',
        ...dimOf(dimmed)
      }}
    >
      <div
        data-lane-fill={platform}
        style={{
          position: 'relative',
          width: `${pct}%`,
          height: '100%',
          borderRadius: '9999px',
          background: color,
          opacity: platform === 'youtube' ? LANE_YOUTUBE_FILL_OPACITY : 1,
          overflow: 'hidden',
          transition: 'width 180ms ease-out'
        }}
      >
        {glint && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
              backgroundSize: '220% 100%',
              backgroundRepeat: 'no-repeat',
              animation: 'spPollGlint 2s linear infinite'
            }}
          />
        )}
      </div>
    </div>
  </div>
)

const WinnerMarks = ({ twitch, youtube, height, top, right }: {
  twitch: boolean
  youtube: boolean
  height: number
  top: number
  right: number
}): JSX.Element => (
  <div
    data-winner-marks={[twitch ? 'twitch' : '', youtube ? 'youtube' : ''].filter((x) => x !== '').join(' ')}
    style={{
      position: 'absolute',
      top: `${top}px`,
      right: `${right}px`,
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      pointerEvents: 'none'
    }}
  >
    {twitch && <VoteMark platform='twitch' height={height} />}
    {youtube && <VoteMark platform='youtube' height={height} />}
  </div>
)

const DimmableBackground = ({ background, dimmed }: { background: string, dimmed: boolean }): JSX.Element => (
  <div
    aria-hidden='true'
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      background,
      ...dimOf(dimmed)
    }}
  />
)

const POLL_WINNER_KEYFRAMES = `
@keyframes spPollGlint { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes spPollPulse { 0%, 100% { opacity: 0.62; } 50% { opacity: 1; } }
@keyframes spPollFadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const LockIcon = ({ size }: { size?: number }): JSX.Element => {
  const edge = size == null ? String(PILL_ICON_SIZE) : String(size)
  return (
    <svg width={edge} height={edge} viewBox='0 0 24 24' fill='none' aria-hidden='true' style={size == null ? { display: 'block' } : { display: 'block', flexShrink: 0 }}>
      <rect x='5' y='11' width='14' height='9' rx='2.5' fill='currentColor' />
      <path d='M8 11V8a4 4 0 0 1 8 0v3' stroke='currentColor' strokeWidth='2.2' fill='none' />
    </svg>
  )
}

const PollClosedPill = ({ fontWeight, fontSize }: { fontWeight: number, fontSize?: number }): JSX.Element => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '7px 18px',
      borderRadius: '9999px',
      background: 'rgba(0,0,0,0.38)',
      border: '1px solid rgba(255,255,255,0.18)',
      color: '#FFFFFF',
      fontWeight,
      fontSize: '16px',
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      opacity: 0,
      animation: 'spPollFadeIn 500ms ease-out forwards',
      ...(fontSize == null
        ? {}
        : { fontSize: `${fontSize}px`, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%', textAlign: 'center' })
    }}
  >
    <LockIcon size={fontSize == null ? undefined : scaleBy(fontSize, PILL_ICON_SIZE, PILL_FONT_SIZE)} />
    Poll Closed • Results Final
  </span>
)

const PollWinnerGlow = ({ color, edge = 'inset' }: { color: string, edge?: 'inset' | 'full' }): JSX.Element => {
  const ringInset = edge === 'full' ? '0px' : '3px 12px'
  const ringRadius = edge === 'full' ? '0px' : '16px'
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0,
        animation: 'spPollFadeIn 500ms ease-out forwards'
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: ringInset,
          borderRadius: ringRadius,
          border: `2.5px solid ${color}`,
          boxShadow: `0 0 16px ${color}, 0 0 5px ${color}, inset 0 0 12px ${color}55`,
          animation: 'spPollPulse 2.2s ease-in-out infinite'
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: ringInset,
          borderRadius: ringRadius,
          padding: '2.5px',
          background: 'linear-gradient(110deg, transparent 38%, rgba(255,255,255,0.92) 50%, transparent 62%)',
          backgroundSize: '220% 100%',
          backgroundRepeat: 'no-repeat',
          animation: 'spPollGlint 2.6s linear infinite',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          maskComposite: 'exclude'
        }}
      />
    </div>
  )
}

export const PollPreview = ({
  draft,
  live,
  layoutWidth = POLL.width
}: {
  draft: PollDraft
  live?: PollLiveData
  layoutWidth?: number
}): JSX.Element => {
  const fontFamily = elementFontStack(draft.fontKey)
  const fontReady = useFontGate(draft.fontKey)
  const hasBgImage = draft.backgroundImage.trim().length > 0
  const barWidth = Math.max(120, layoutWidth - (POLL.width - POLL.option.barWidth))
  const optionTextMaxWidth = layoutWidth - (POLL.width - POLL.option.textMaxWidth)
  const ctaTextMaxWidth = layoutWidth - (POLL.width - POLL.callToAction.textMaxWidth)
  const singlePcts = live == null ? [] : largestRemainderPercents(live.optionVoteCounts)
  const pctFor = (i: number): number => singlePcts[i] ?? 0
  const altBg = draft.alternatingOptionBackground.trim().length > 0
    ? draft.alternatingOptionBackground
    : draft.optionsBackground
  const laneMode: VoteLaneMode = live == null
    ? 'TWITCH'
    : (live.laneMode ?? (live.hasYouTubeVotes === true ? 'SPLIT' : 'TWITCH'))
  const split = laneMode === 'SPLIT'
  const byPlatform = live?.optionVoteCountsByPlatform ?? []
  const twitchCounts = split ? draft.options.map((_, i) => byPlatform[i]?.twitch ?? live?.optionVoteCounts[i] ?? 0) : []
  const youtubeCounts = split ? draft.options.map((_, i) => byPlatform[i]?.youtube ?? 0) : []
  const twitchPcts = largestRemainderPercents(twitchCounts)
  const youtubePcts = largestRemainderPercents(youtubeCounts)
  const youtubeTotal = sumCounts(youtubeCounts)
  const closed = live?.votingClosed === true && (live.totalVotes > 0 || youtubeTotal > 0)
  const topCount = closed && live != null && !split ? Math.max(...live.optionVoteCounts) : -1
  const firstWinner = topCount > -1 && live != null ? live.optionVoteCounts.indexOf(topCount) : -1
  const splitWinners = split && closed ? splitWinnersOf(twitchCounts, youtubeCounts) : null
  const splitGlows = (i: number): boolean => splitWinners != null &&
    (splitWinners.twitch.includes(i) || (!splitWinners.agree && splitWinners.youtube.includes(i)))
  const splitMarks = (i: number): { twitch: boolean, youtube: boolean } | null =>
    splitWinners == null || splitWinners.agree || !splitGlows(i)
      ? null
      : { twitch: splitWinners.twitch.includes(i), youtube: splitWinners.youtube.includes(i) }
  if (!fontReady) return <CenteredOnCanvas width={layoutWidth}>{null}</CenteredOnCanvas>
  if (draft.template === 'TICKER') {
    const t = tickerMetricsFor(draft)
    const eyebrowOn = draft.tickerLabel.trim().length > 0
    const titleSize = t.segment.title[draft.tickerTextSize]
    const storedWeights = draft.tickerBlockWeights ?? null
    const weights = storedWeights !== null && storedWeights.length === draft.options.length + 2
      ? storedWeights
      : null
    const weightSum = weights !== null
      ? weights.reduce((s, x) => s + x, 0)
      : 0
    const middleWeight = weights !== null
      ? weights.slice(1, -1).reduce((s, x) => s + x, 0)
      : 0
    const leftFlex = weights !== null ? `${weights[0]} 1 ${(weights[0] / weightSum) * 100}%` : `0 1 ${t.left.width}px`
    const middleFlex = weights !== null ? `${middleWeight} 1 ${(middleWeight / weightSum) * 100}%` : '1 1 0'
    const ctaFlex = weights !== null ? `${weights[weights.length - 1]} 1 ${(weights[weights.length - 1] / weightSum) * 100}%` : `0 1 ${t.cta.width}px`
    return (
      <CenteredOnCanvas width={layoutWidth}>
        <div
          data-testid='poll-preview'
          style={{
            position: 'relative',
            width: '100%',
            fontFamily,
            overflow: 'hidden',
            borderRadius: `${draft.cornerRadius}px`,
            border: `${draft.borderWidth ?? DEFAULT_BORDER_WIDTH}px solid ${draft.borderColor ?? DEFAULT_BORDER_COLOR}`
          }}
        >
          {hasBgImage && (
            <img
              src={draft.backgroundImage}
              alt=''
              aria-hidden='true'
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: fitToObjectFit(draft.backgroundImageFit)
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
            />
          )}
          {closed && <style>{POLL_WINNER_KEYFRAMES}</style>}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              minHeight: `${t.minHeight}px`
            }}
          >
            <div
              style={{
                flex: leftFlex,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: `${t.left.paddingY}px ${t.left.paddingX}px`,
                background: draft.headingBackground,
                color: draft.headingTextColor,
                overflow: 'hidden'
              }}
            >
              {eyebrowOn && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${t.left.eyebrow.gap}px`,
                    marginBottom: `${t.left.eyebrow.marginBottom}px`
                  }}
                >
                  <span
                    style={{
                      width: `${t.left.eyebrow.dotSize}px`,
                      height: `${t.left.eyebrow.dotSize}px`,
                      borderRadius: '9999px',
                      background: t.left.eyebrow.dotColor,
                      flexShrink: 0
                    }}
                  />
                  <span
                    style={{
                      fontSize: `${t.left.eyebrow.fontSize}px`,
                      lineHeight: `${t.left.eyebrow.lineHeight}px`,
                      letterSpacing: t.left.eyebrow.letterSpacing,
                      fontWeight: t.left.eyebrow.fontWeight,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      ...(draft.tickerLabelFontSize == null
                        ? {}
                        : { whiteSpace: 'normal', overflowWrap: 'anywhere' })
                    }}
                  >
                    {draft.tickerLabel}
                  </span>
                </div>
              )}
              <div
                style={{
                  fontSize: `${t.left.question.fontSize}px`,
                  lineHeight: `${t.left.question.lineHeight}px`,
                  fontWeight: POLL.heading.fontWeight,
                  wordBreak: 'break-word'
                }}
              >
                {draft.question.length > 0 ? draft.question : 'Your poll question'}
              </div>
            </div>
            <div style={{ flex: middleFlex, minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
              {draft.options.map((o, i) => {
                const fallbackBg = i % 2 === 0 ? draft.optionsBackground : altBg
                const segBg = o.backgroundColor.trim().length > 0 ? o.backgroundColor : fallbackBg
                const pct = pctFor(i)
                const filledFraction = (live != null) ? pct / 100 : 0.08
                const pctLabel = (live != null) ? `${pct}%` : '0%'
                const countLabel = pctLabel
                const segFlex = weights !== null ? `${weights[i + 1]} 1 ${(weights[i + 1] / middleWeight) * 100}%` : '1 1 0'
                if (split) {
                  const glows = splitGlows(i)
                  const dimmed = closed && !glows
                  const marks = splitMarks(i)
                  return (
                    <div
                      key={i}
                      data-split-option={i}
                      style={{
                        position: 'relative',
                        isolation: 'isolate',
                        flex: segFlex,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: `${t.segment.paddingY}px ${t.segment.paddingX}px`,
                        color: o.textColor,
                        overflow: 'hidden'
                      }}
                    >
                      <DimmableBackground background={segBg} dimmed={dimmed} />
                      {glows && <PollWinnerGlow color={draft.winnerOutlineColor} edge='full' />}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: `${t.segment.gap}px`
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: `${t.segment.chip.minWidth}px`,
                            height: `${t.segment.chip.height}px`,
                            padding: `0 ${t.segment.chip.paddingX}px`,
                            borderRadius: `${t.segment.chip.radius}px`,
                            background: 'rgba(0,0,0,0.35)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            fontSize: `${t.segment.chip.fontSize}px`,
                            fontWeight: t.segment.chip.fontWeight,
                            whiteSpace: 'nowrap',
                            ...dimOf(dimmed)
                          }}
                        >
                          {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            wordBreak: 'break-word',
                            fontSize: `${titleSize.fontSize}px`,
                            lineHeight: `${titleSize.lineHeight}px`,
                            fontWeight: titleSize.fontWeight,
                            ...dimOf(dimmed)
                          }}
                        >
                          {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                        </span>
                        <span
                          data-split-figures={i}
                          style={{
                            flexShrink: 0,
                            fontSize: `${t.segment.percent.fontSize}px`,
                            lineHeight: `${t.segment.percent.lineHeight}px`,
                            fontWeight: t.segment.percent.fontWeight,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <SplitFigures
                            twitchPct={twitchPcts[i] ?? 0}
                            youtubePct={youtubePcts[i] ?? 0}
                            markHeight={SPLIT_TICKER.figureMarkHeight}
                            dimmed={dimmed}
                          />
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: `${t.segment.bar.marginTop}px`,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: `${SPLIT_TICKER.laneGap}px`
                        }}
                      >
                        <VoteLane platform='twitch' pct={twitchPcts[i] ?? 0} color={o.progressBarColor} trackHeight={t.segment.bar.height} metrics={SPLIT_TICKER} glint={glows} dimmed={dimmed} />
                        <VoteLane platform='youtube' pct={youtubePcts[i] ?? 0} color={o.progressBarColor} trackHeight={t.segment.bar.height} metrics={SPLIT_TICKER} glint={glows} dimmed={dimmed} />
                      </div>
                      {marks != null && (
                        <WinnerMarks twitch={marks.twitch} youtube={marks.youtube} height={SPLIT_TICKER.winnerMarkHeight} top={5} right={6} />
                      )}
                    </div>
                  )
                }
                const winnerIndex = topCount > -1 && (live?.optionVoteCounts[i] ?? -1) === topCount ? i : firstWinner
                return (
                  <div
                    key={i}
                    style={{
                      position: 'relative',
                      flex: weights !== null ? `${weights[i + 1]} 1 ${(weights[i + 1] / middleWeight) * 100}%` : '1 1 0',
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      padding: `${t.segment.paddingY}px ${t.segment.paddingX}px`,
                      background: segBg,
                      color: o.textColor,
                      overflow: 'hidden',
                      opacity: closed && winnerIndex !== -1 && i !== winnerIndex ? 0.45 : 1,
                      transition: 'opacity 350ms ease'
                    }}
                  >
                    {closed && i === winnerIndex && (
                      <PollWinnerGlow color={draft.winnerOutlineColor} edge='full' />
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: `${t.segment.gap}px`
                      }}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: `${t.segment.chip.minWidth}px`,
                          height: `${t.segment.chip.height}px`,
                          padding: `0 ${t.segment.chip.paddingX}px`,
                          borderRadius: `${t.segment.chip.radius}px`,
                          background: 'rgba(0,0,0,0.35)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          fontSize: `${t.segment.chip.fontSize}px`,
                          fontWeight: t.segment.chip.fontWeight,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          wordBreak: 'break-word',
                          fontSize: `${titleSize.fontSize}px`,
                          lineHeight: `${titleSize.lineHeight}px`,
                          fontWeight: titleSize.fontWeight
                        }}
                      >
                        {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                      </span>
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: `${t.segment.percent.fontSize}px`,
                          lineHeight: `${t.segment.percent.lineHeight}px`,
                          fontWeight: t.segment.percent.fontWeight,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {countLabel}
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: `${t.segment.bar.marginTop}px`,
                        height: `${t.segment.bar.height}px`,
                        borderRadius: '9999px',
                        background: 'rgba(255,255,255,0.15)',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          width: `${filledFraction * 100}%`,
                          height: '100%',
                          borderRadius: '9999px',
                          background: o.progressBarColor,
                          overflow: 'hidden',
                          transition: 'width 180ms ease-out'
                        }}
                      >
                        {closed && i === winnerIndex && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
                              backgroundSize: '220% 100%',
                              backgroundRepeat: 'no-repeat',
                              animation: 'spPollGlint 2s linear infinite'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div
              style={{
                flex: ctaFlex,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: `${t.cta.paddingY}px ${t.cta.paddingX}px`,
                background: draft.callToActionBackground,
                color: draft.callToActionTextColor,
                overflow: 'hidden'
              }}
            >
              {closed
                ? <PollClosedPill fontWeight={elementPinnedWeight(draft.fontKey, 600)} fontSize={draft.closedTextFontSize ?? undefined} />
                : (
                  <>
                    {draft.callToActionText.trim().length > 0 && (
                      <div
                        style={{
                          fontSize: `${t.cta.fontSize}px`,
                          lineHeight: `${t.cta.lineHeight}px`,
                          fontWeight: t.cta.fontWeight
                        }}
                      >
                        {draft.callToActionText}
                      </div>
                    )}
                    {draft.ctaSubtext.trim().length > 0 && (
                      <div
                        style={{
                          marginTop: `${t.cta.subtext.marginTop}px`,
                          fontSize: `${t.cta.subtext.fontSize}px`,
                          lineHeight: `${t.cta.subtext.lineHeight}px`,
                          fontWeight: t.cta.subtext.fontWeight,
                          opacity: t.cta.subtext.opacity
                        }}
                      >
                        {draft.ctaSubtext}
                      </div>
                    )}
                  </>
                  )}
            </div>
          </div>
        </div>
      </CenteredOnCanvas>
    )
  }
  const q = draft.questionFontSize
  const heading = q == null
    ? POLL.heading
    : { ...POLL.heading, fontSize: q, lineHeight: scaleBy(q, POLL.heading.lineHeight, POLL.heading.fontSize) }
  const rowAlign = draft.commandFontSize == null && draft.displayTextFontSize == null && draft.numberFontSize == null
    ? 'flex-start'
    : 'baseline'
  return (
    <CenteredOnCanvas width={layoutWidth}>
      <div
        data-testid='poll-preview'
        style={{
          position: 'relative',
          width: '100%',
          fontFamily,
          overflow: 'hidden',
          borderRadius: `${draft.cornerRadius}px`,
          border: `${draft.borderWidth ?? DEFAULT_BORDER_WIDTH}px solid ${draft.borderColor ?? DEFAULT_BORDER_COLOR}`
        }}
      >
        {hasBgImage && (
          <img
            src={draft.backgroundImage}
            alt=''
            aria-hidden='true'
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: fitToObjectFit(draft.backgroundImageFit)
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
        )}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              paddingTop: `${POLL.heading.paddingTop}px`,
              paddingBottom: `${POLL.heading.paddingBottom}px`,
              paddingLeft: `${POLL.heading.paddingLeft}px`,
              paddingRight: `${POLL.heading.paddingRight}px`,
              background: draft.headingBackground,
              color: draft.headingTextColor,
              fontWeight: POLL.heading.fontWeight,
              fontSize: `${heading.fontSize}px`,
              lineHeight: `${heading.lineHeight}px`,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span>
              {draft.question.length > 0 ? draft.question : 'Your poll question'}
            </span>
          </div>
          {closed && <style>{POLL_WINNER_KEYFRAMES}</style>}
          <div>
            {draft.options.map((o, i) => {
              const fallbackBg = i % 2 === 0 ? draft.optionsBackground : altBg
              const rowBg = o.backgroundColor.trim().length > 0 ? o.backgroundColor : fallbackBg
              const pct = pctFor(i)
              const filledFraction = (live != null) ? pct / 100 : 0.08
              const filledWidth = filledFraction * barWidth
              const pctLabel = (live != null) ? `${pct}%` : '0%'
              const countLabel = pctLabel
              if (split) {
                const glows = splitGlows(i)
                const dimmed = closed && !glows
                const marks = splitMarks(i)
                return (
                  <div
                    key={i}
                    data-split-option={i}
                    style={{
                      position: 'relative',
                      isolation: 'isolate',
                      color: draft.optionsTextColor,
                      paddingTop: `${POLL.option.paddingTop}px`,
                      paddingBottom: `${POLL.option.paddingBottom}px`,
                      paddingLeft: `${POLL.option.paddingLeft}px`,
                      paddingRight: `${POLL.option.paddingRight}px`
                    }}
                  >
                    <DimmableBackground background={rowBg} dimmed={dimmed} />
                    {glows && <PollWinnerGlow color={draft.winnerOutlineColor} />}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: rowAlign,
                        justifyContent: 'space-between',
                        gap: `${POLL.option.chatCommandGap}px`,
                        fontWeight: POLL.option.fontWeight,
                        fontSize: `${POLL.option.fontSize}px`,
                        lineHeight: `${POLL.option.lineHeight}px`,
                        color: o.textColor
                      }}
                    >
                      <span style={{ flexShrink: 0, ...classicSpanSize(draft.commandFontSize), ...dimOf(dimmed) }}>
                        {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          maxWidth: `${optionTextMaxWidth}px`,
                          wordBreak: 'break-word',
                          ...classicSpanSize(draft.displayTextFontSize),
                          ...dimOf(dimmed)
                        }}
                      >
                        {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                      </span>
                      <span data-split-figures={i} style={{ flexShrink: 0, whiteSpace: 'nowrap', ...classicSpanSize(draft.numberFontSize) }}>
                        <SplitFigures
                          twitchPct={twitchPcts[i] ?? 0}
                          youtubePct={youtubePcts[i] ?? 0}
                          markHeight={SPLIT_CLASSIC.figureMarkHeight}
                          dimmed={dimmed}
                        />
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop: `${POLL.option.textToBarGap}px`,
                        marginLeft: 'auto',
                        width: `${barWidth + SPLIT_CLASSIC.markColumn + SPLIT_CLASSIC.markGap}px`,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${SPLIT_CLASSIC.laneGap}px`
                      }}
                    >
                      <VoteLane platform='twitch' pct={twitchPcts[i] ?? 0} color={o.progressBarColor} trackHeight={POLL.option.barHeight} metrics={SPLIT_CLASSIC} glint={glows} dimmed={dimmed} />
                      <VoteLane platform='youtube' pct={youtubePcts[i] ?? 0} color={o.progressBarColor} trackHeight={POLL.option.barHeight} metrics={SPLIT_CLASSIC} glint={glows} dimmed={dimmed} />
                    </div>
                    {marks != null && (
                      <WinnerMarks twitch={marks.twitch} youtube={marks.youtube} height={SPLIT_CLASSIC.winnerMarkHeight} top={8} right={30} />
                    )}
                  </div>
                )
              }
              const winnerIndex = topCount > -1 && (live?.optionVoteCounts[i] ?? -1) === topCount ? i : firstWinner
              return (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    background: rowBg,
                    color: draft.optionsTextColor,
                    paddingTop: `${POLL.option.paddingTop}px`,
                    paddingBottom: `${POLL.option.paddingBottom}px`,
                    paddingLeft: `${POLL.option.paddingLeft}px`,
                    paddingRight: `${POLL.option.paddingRight}px`,
                    opacity: closed && winnerIndex !== -1 && i !== winnerIndex ? 0.45 : 1,
                    transition: 'opacity 350ms ease'
                  }}
                >
                  {closed && i === winnerIndex && (
                    <PollWinnerGlow color={draft.winnerOutlineColor} />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: rowAlign,
                      justifyContent: 'space-between',
                      gap: `${POLL.option.chatCommandGap}px`,
                      fontWeight: POLL.option.fontWeight,
                      fontSize: `${POLL.option.fontSize}px`,
                      lineHeight: `${POLL.option.lineHeight}px`,
                      color: o.textColor
                    }}
                  >
                    <span style={{ flexShrink: 0, ...classicSpanSize(draft.commandFontSize) }}>
                      {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        maxWidth: `${optionTextMaxWidth}px`,
                        wordBreak: 'break-word',
                        ...classicSpanSize(draft.displayTextFontSize)
                      }}
                    >
                      {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                    </span>
                    <span style={{ flexShrink: 0, ...classicSpanSize(draft.numberFontSize) }}>{countLabel}</span>
                  </div>
                  <div
                    style={{
                      marginTop: `${POLL.option.textToBarGap}px`,
                      marginLeft: 'auto',
                      width: `${barWidth}px`,
                      height: `${POLL.option.barHeight}px`,
                      borderRadius: '9999px',
                      background: 'rgba(255,255,255,0.15)',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: `${filledWidth}px`,
                        height: '100%',
                        borderRadius: '9999px',
                        background: o.progressBarColor,
                        overflow: 'hidden',
                        transition: 'width 180ms ease-out'
                      }}
                    >
                      {closed && i === winnerIndex && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
                            backgroundSize: '220% 100%',
                            backgroundRepeat: 'no-repeat',
                            animation: 'spPollGlint 2s linear infinite'
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div
            style={{
              minHeight: `${POLL.callToAction.minHeight}px`,
              paddingTop: `${POLL.callToAction.paddingTop}px`,
              paddingBottom: `${POLL.callToAction.paddingBottom}px`,
              background: draft.callToActionBackground,
              color: draft.callToActionTextColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {closed
              ? (
                <PollClosedPill fontWeight={elementPinnedWeight(draft.fontKey, 600)} fontSize={draft.closedTextFontSize ?? undefined} />
                )
              : (draft.callToActionText.trim().length > 0 && (
                <span
                  style={{
                    maxWidth: `${ctaTextMaxWidth}px`,
                    textAlign: 'center',
                    fontWeight: POLL.callToAction.fontWeight,
                    fontSize: `${draft.callToActionFontSize ?? POLL.callToAction.fontSize}px`
                  }}
                >
                  {draft.callToActionText}
                </span>
                ))}
          </div>
        </div>
      </div>
    </CenteredOnCanvas>
  )
}

const SAMPLE_WINNER_SPREAD = [64, 27, 9, 4, 2, 1, 1, 1]
export const sampleClosedPollLive = (optionCount: number): PollLiveData => {
  const counts = SAMPLE_WINNER_SPREAD.slice(0, Math.max(optionCount, 1))
  return {
    totalVotes: counts.reduce((sum, c) => sum + c, 0),
    optionVoteCounts: counts,
    votingClosed: true
  }
}

export const draftFromPoll = (poll: PollSummary): PollDraft => ({
  name: poll.name,
  question: poll.question,
  callToActionText: poll.callToActionText ?? '',
  options: poll.options.map((o) => ({
    displayText: o.displayText,
    command: o.command,
    textColor: o.textColor,
    progressBarColor: o.progressBarColor,
    backgroundColor: o.backgroundColor ?? ''
  })),
  template: poll.template ?? 'CLASSIC',
  tickerLabel: poll.tickerLabel ?? '',
  ctaSubtext: poll.ctaSubtext ?? '',
  tickerTextSize: poll.tickerTextSize ?? 'MEDIUM',
  tickerBlockWeights: poll.tickerBlockWeights ?? null,
  headingTextColor: poll.headingTextColor,
  headingBackground: poll.headingBackground,
  optionsTextColor: poll.optionsTextColor,
  optionsBackground: poll.optionsBackground,
  callToActionTextColor: poll.callToActionTextColor,
  callToActionBackground: poll.callToActionBackground,
  alternatingOptionBackground: poll.alternatingOptionBackground ?? poll.optionsBackground,
  backgroundImage: poll.backgroundImage ?? '',
  backgroundImageFit: poll.backgroundImageFit ?? 'COVER',
  cornerRadius: poll.cornerRadius,
  borderWidth: poll.borderWidth ?? DEFAULT_BORDER_WIDTH,
  borderColor: poll.borderColor ?? DEFAULT_BORDER_COLOR,
  winnerOutlineColor: poll.winnerOutlineColor ?? DEFAULT_WINNER_OUTLINE_COLOR,
  fontKey: normalizeChatFontKey(poll.fontKey ?? 'DEFAULT'),
  questionFontSize: poll.questionFontSize ?? null,
  tickerLabelFontSize: poll.tickerLabelFontSize ?? null,
  displayTextFontSize: poll.displayTextFontSize ?? null,
  commandFontSize: poll.commandFontSize ?? null,
  numberFontSize: poll.numberFontSize ?? null,
  callToActionFontSize: poll.callToActionFontSize ?? null,
  closedTextFontSize: poll.closedTextFontSize ?? null,
  tickerHeight: poll.tickerHeight ?? null
})
